/**
 * Timeline Query Keys
 *
 * Centralized query key factory for all timeline/history-related queries.
 */

import type { TimelineFilters } from './types';

/**
 * Timeline query key factory
 *
 * @example
 * ```typescript
 * // Get matter timeline
 * useQuery({ queryKey: timelineKeys.forMatter(matterId), queryFn: ... })
 *
 * // Get recent activity
 * useQuery({ queryKey: timelineKeys.recent(matterId), queryFn: ... })
 * ```
 */
export const timelineKeys = {
  /**
   * Root key for all timeline queries
   */
  all: ['timeline-events'] as const,

  /**
   * Timeline events for a specific matter
   * @param matterId - The matter ID
   */
  forMatter: (matterId: number) => [...timelineKeys.all, matterId] as const,

  /**
   * Filtered timeline list
   * @param matterId - The matter ID
   * @param filters - Optional filters
   */
  list: (matterId: number, filters?: TimelineFilters) =>
    filters
      ? ([...timelineKeys.forMatter(matterId), filters] as const)
      : timelineKeys.forMatter(matterId),

  /**
   * Grouped timeline events
   * @param matterId - The matter ID
   */
  grouped: (matterId: number) =>
    [...timelineKeys.forMatter(matterId), 'grouped'] as const,

  /**
   * Recent activity (limited set)
   * @param matterId - The matter ID
   * @param limit - Number of events to fetch
   */
  recent: (matterId: number, limit?: number) =>
    [...timelineKeys.forMatter(matterId), 'recent', limit] as const,

  /**
   * History (alias for timeline)
   * @param matterId - The matter ID
   */
  history: (matterId: number) => ['history', matterId] as const
} as const;
